import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { GoogleLogin as GoogleLoginButton } from "@react-oauth/google";
import {
  ArrowRight,
  Building2,
  Eye,
  EyeOff,
  GraduationCap,
  Lock,
  Phone,
  Presentation,
  ShieldCheck,
  User,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { login } from "../../utils/auth";
import { DEFAULT_PHONE_COUNTRY, canonicalPhone, countryOfTyped } from "../../utils/phone";
import useAudienceTerm from "../../hooks/useAudienceTerm";
import useGoogleAuth from "../../hooks/useGoogleAuth";
import { useBranding } from "../../contexts/BrandingContext";
import InstitutePicker from "./InstitutePicker";
import TwoFactorModal from "./TwoFactorModal";
import RoleTiles from "./RoleTiles";
import { AppButton, Callout, CountryCodeSelect, FormField, GoogleIcon, IconButton, Input, Label } from "../ui";

// What has been typed so far reads as a phone number rather than an email.
const looksLikePhone = (value) => /^[\d\s+().-]+$/.test(value.trim());

const ROLES = [
  { key: "student", label: "Student", icon: GraduationCap },
  { key: "instructor", label: "Instructor", icon: Presentation },
  { key: "admin", label: "Admin", icon: Building2 },
  { key: "superadmin", label: "Super Admin", icon: ShieldCheck },
];

function useRoles() {
  const term = useAudienceTerm();
  return useMemo(
    () => ROLES.map((r) => (r.key === "student" ? { ...r, label: term.loginLabel } : r)),
    [term.loginLabel]
  );
}

// Areas each usertype is allowed to be sent to by a ?redirect= deep link.
// Anything outside the caller's own area is ignored (ProtectedRoute would only
// bounce them to /not-authorized), as is any non-relative/protocol URL.
const ROLE_AREAS = {
  student: ["/s/"],
  instructor: ["/i/"],
  tenantadmin: ["/sa"],
};
const ALL_AREAS = ["/s/", "/i/", "/sa"];

const resolveRedirect = (path, usertype) => {
  // Only same-origin paths — "//evil.com" and "/\evil.com" are off-site
  if (!path || !path.startsWith("/") || path.startsWith("//") || path.startsWith("/\\")) return null;
  const areas = ROLE_AREAS[usertype];
  if (areas) return areas.some((p) => path.startsWith(p)) ? path : null;
  // admin / superadmin — anything that isn't another role's area
  return ALL_AREAS.some((p) => path.startsWith(p)) ? null : path;
};

const Login = () => {
  const branding = useBranding();
  const roles = useRoles();
  const { refreshBranding } = branding;

  const [selectedRole, setSelectedRole] = useState(null);
  const [creds, setCreds] = useState({ identifier: "", password: "" });
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [institutes, setInstitutes] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [twoFactorMethod, setTwoFactorMethod] = useState(null);

  // Google multi-institute retry state
  const [pendingGoogleToken, setPendingGoogleToken] = useState(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get("redirect") || "";

  // ── Google Login ──────────────────────────────────────────────────────────
  const doGoogleLogin = useCallback(async (idToken, instituteId) => {
    setGoogleLoading(true);
    setError("");
    try {
      const usertype = selectedRole?.key || "student";
      const payload = { idToken, usertype };
      if (instituteId) payload.instituteId = instituteId;

      const { data } = await apiClient.post("/auth/google-login", payload);

      if (data.multiInstitute) {
        setPendingGoogleToken(idToken);
        setInstitutes(data.institutes);
        return;
      }

      handleLoginResponse(data);
    } catch (err) {
      const resp = err?.response;
      if (resp?.status === 300 && resp?.data?.multiInstitute) {
        setPendingGoogleToken(idToken);
        setInstitutes(resp.data.institutes);
      } else if (resp?.status === 404 && resp?.data?.notRegistered) {
        setError(resp.data.message);
      } else {
        setError(resp?.data?.message || "Google login failed!");
      }
    } finally {
      setGoogleLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole]);

  const onGoogleCredential = useCallback((credential) => {
    doGoogleLogin(credential);
  }, [doGoogleLogin]);

  const onGoogleError = useCallback((msg) => {
    setError(typeof msg === "string" ? msg : "Google login was cancelled or failed.");
  }, []);

  const { isDirectMode, openPopup, hasGoogleAuth } = useGoogleAuth(
    onGoogleCredential,
    onGoogleError
  );

  useEffect(() => {
    const reason = sessionStorage.getItem("logoutReason");
    if (reason) {
      setError(reason);
      sessionStorage.removeItem("logoutReason");
    }
  }, []);

  useEffect(() => {
    if (selectedRole) localStorage.setItem("selectedRole", JSON.stringify(selectedRole));
  }, [selectedRole]);

  useEffect(() => {
    // Only the KEY is read back. The stored object predates the move from emoji
    // to lucide icons, so a role rehydrated wholesale would carry an `icon` that
    // is a string where a component is expected; resolving the key against ROLES
    // keeps one definition of what a role looks like.
    const stored = localStorage.getItem("selectedRole");
    if (!stored) return;
    try {
      const match = ROLES.find((r) => r.key === JSON.parse(stored)?.key);
      if (match) setSelectedRole(match);
    } catch {
      localStorage.removeItem("selectedRole");
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // The identifier field accepts either an email or a phone number, so it must
    // not strip non-digits (that would make typing an email impossible).
    setCreds((prev) => ({ ...prev, [name]: value }));
    // A number typed with its own "+" shows its country in the picker.
    if (name === "identifier") {
      const typed = countryOfTyped(value);
      if (typed) setPhoneCountry(typed.country);
    }
    setError("");
  };

  // Decide whether the typed identifier is an email or a phone, and normalise it
  // to what the BE stores: a lower-cased email, or the number in its stored
  // shape — 10 digits for India, "+E.164" for the country picked (utils/phone).
  const buildIdentifierPayload = (identifier) => {
    const value = (identifier || "").trim();
    if (value.includes("@")) {
      return { email: value.toLowerCase() };
    }
    return { phone: canonicalPhone(value, phoneCountry) };
  };

  const isPhoneIdentifier = looksLikePhone(creds.identifier);

  const completeLogin = (data) => {
    setInstitutes(null);
    login(data);
    if (refreshBranding) refreshBranding();
    // Deep link (e.g. "Join Live Class" from an email) wins over the role landing page
    const target = resolveRedirect(redirectParam, data.user.usertype);
    if (target) return navigate(target, { replace: true });
    if (data.user.usertype === "tenantadmin") navigate("/sa");
    else if (data.user.usertype === "student") navigate("/s/dashboard");
    else if (data.user.usertype === "instructor") navigate("/i/");
    else if (data.user.usertype === "admin" || data.user.usertype === "superadmin") navigate("/dashboard");
    else navigate("/");
  };

  const handleLoginResponse = (data) => {
    if (data.requires2FA) {
      setTempToken(data.tempToken);
      setTwoFactorMethod(data.twoFactorMethod);
      setShow2FA(true);
      return;
    }
    if (data.token) {
      completeLogin(data);
    } else {
      setError("Invalid response from server.");
    }
  };

  const doLogin = async (extraPayload = {}) => {
    setLoading(true);
    setError("");
    try {
      const usertype = selectedRole?.key || "student";
      const payload = {
        ...buildIdentifierPayload(creds.identifier),
        password: creds.password,
        usertype,
        ...extraPayload,
      };
      const { data } = await apiClient.post("/auth/login", payload);
      handleLoginResponse(data);
    } catch (err) {
      const resp = err?.response;
      if (resp?.status === 300 && resp?.data?.multiInstitute) {
        setPendingGoogleToken(null);
        setInstitutes(resp.data.institutes);
      } else {
        setError(resp?.data?.message || "Login failed!");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await doLogin();
  };

  const handleInstitutePick = (inst) => {
    setInstitutes(null);
    if (pendingGoogleToken) {
      doGoogleLogin(pendingGoogleToken, inst.instituteId);
    } else {
      doLogin({ instituteId: inst.instituteId });
    }
  };

  const handleGoogleSuccess = (credentialResponse) => {
    if (!selectedRole) {
      setError("Please select your role above before signing in with Google.");
      return;
    }
    if (credentialResponse.credential) {
      doGoogleLogin(credentialResponse.credential);
    } else {
      setError("Failed to get Google credential.");
    }
  };

  const handleGoogleError = () => {
    setError("Google login was cancelled or failed.");
  };

  const handleOpenGooglePopup = () => {
    if (!selectedRole) {
      setError("Please select your role above before signing in with Google.");
      return;
    }
    openPopup();
  };

  const handle2FAComplete = (data) => {
    setShow2FA(false);
    setTempToken(null);
    completeLogin(data);
  };

  return (
    <>
      <div>
        <header className="tw-mb-7 tw-text-center">
          <h1 className="tw-mb-1.5 tw-text-[1.75rem] tw-font-bold tw-tracking-[-0.02em] tw-text-content">
            Welcome back!
          </h1>
          <p className="tw-mb-0 tw-text-sm tw-text-content-muted">
            Sign in to continue your learning journey
          </p>
        </header>

        <div className="tw-mb-5">
          <Label id="login-role-label" className="tw-mb-2 tw-block">
            Select your role
          </Label>
          <RoleTiles
            roles={roles}
            value={selectedRole?.key || null}
            onSelect={setSelectedRole}
            labelId="login-role-label"
          />
        </div>

        <form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-4">
          <FormField label="Email or Phone Number" htmlFor="login-identifier">
            {/* The country picker joins once the value reads as a number. Its
                slot is always there (null or not) so the input keeps its place
                in the tree and does not lose focus mid-word. */}
            <div className="tw-flex tw-items-center tw-gap-2">
              {isPhoneIdentifier ? (
                <CountryCodeSelect
                  size="lg"
                  value={phoneCountry}
                  onChange={setPhoneCountry}
                  aria-label="Phone number country code"
                />
              ) : null}
              <Input
                id="login-identifier"
                size="lg"
                type="text"
                placeholder="Email or mobile number"
                name="identifier"
                autoComplete="username"
                value={creds.identifier}
                onChange={handleChange}
                required
                // Always SOME leading icon: Input renders a different tree
                // without one, which would remount the field on the first digit.
                leading={isPhoneIdentifier ? <Phone size={16} /> : <User size={16} />}
                wrapperClassName="tw-min-w-0 tw-flex-1"
              />
            </div>
          </FormField>

          {/* Not a FormField: the label shares its row with the reset link, which
              the field's own label slot has nowhere to put. */}
          <div className="tw-flex tw-flex-col tw-gap-1.5">
            <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-3">
              <Label htmlFor="login-password">Password</Label>
              <Link
                to="/forgot-password"
                className="tw-text-[0.8125rem] tw-font-medium tw-text-primary tw-no-underline hover:tw-underline"
              >
                Forgot password?
              </Link>
            </div>
            <Input
              id="login-password"
              size="lg"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              name="password"
              autoComplete="current-password"
              value={creds.password}
              onChange={handleChange}
              required
              leading={<Lock size={16} />}
              trailing={
                <IconButton
                  size="sm"
                  label={showPassword ? "Hide password" : "Show password"}
                  icon={showPassword ? Eye : EyeOff}
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                />
              }
            />
          </div>

          {error && (
            <Callout tone="danger" role="alert">
              {error}
            </Callout>
          )}

          {/* AppButton takes no `loading` prop by design — a busy state is
              `disabled` plus a changed label, and the spinner is an ordinary child. */}
          <AppButton
            type="submit"
            size="lg"
            className="tw-h-12 tw-w-full"
            disabled={loading || !selectedRole}
          >
            {loading ? (
              <>
                <Spinner size="sm" animation="border" /> Signing in…
              </>
            ) : (
              <>
                Sign In <ArrowRight size={17} aria-hidden="true" />
              </>
            )}
          </AppButton>

          {/* Google — rendered whenever a client ID is configured. A role is still
              required, because the BE needs a usertype to resolve the account. */}
          {hasGoogleAuth && (
            <>
              <div className="tw-flex tw-items-center tw-gap-3 tw-py-1">
                <span className="tw-h-px tw-flex-1 tw-bg-line" />
                <span className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-caps tw-text-content-subtle">
                  or
                </span>
                <span className="tw-h-px tw-flex-1 tw-bg-line" />
              </div>

              {!selectedRole && (
                <p className="tw-mb-0 tw-text-center tw-text-xs tw-text-content-subtle">
                  Select your role above, then sign in with Google.
                </p>
              )}

              {googleLoading ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-2.5 tw-text-sm tw-text-content-muted">
                  <Spinner size="sm" animation="border" /> Signing in with Google…
                </div>
              ) : isDirectMode && selectedRole ? (
                // Google renders its own button here; the wrapper only sizes it.
                <div className="tw-flex tw-justify-center [&>div]:tw-w-full">
                  <GoogleLoginButton
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    text="continue_with"
                    shape="rectangular"
                    width="100%"
                    theme="outline"
                  />
                </div>
              ) : (
                <AppButton
                  type="button"
                  variant="outline"
                  size="lg"
                  className="tw-h-12 tw-w-full"
                  onClick={handleOpenGooglePopup}
                  disabled={loading || !selectedRole}
                >
                  <GoogleIcon />
                  Continue with Google
                </AppButton>
              )}
            </>
          )}
        </form>

        <p className="tw-mb-0 tw-mt-6 tw-text-center tw-text-sm tw-text-content-muted">
          Don&apos;t have an account?{" "}
          <Link
            to="/signup"
            className="tw-font-semibold tw-text-primary tw-no-underline hover:tw-underline"
          >
            Create a new account
          </Link>
        </p>
      </div>

      {/* Multi-Institute Picker */}
      {institutes && (
        <InstitutePicker
          institutes={institutes}
          onSelect={handleInstitutePick}
          onCancel={() => { setInstitutes(null); setPendingGoogleToken(null); }}
          loading={loading || googleLoading}
          title="Choose Your Institute"
        />
      )}

      {/* Two-Factor Authentication Modal */}
      {show2FA && (
        <TwoFactorModal
          tempToken={tempToken}
          method={twoFactorMethod}
          onSuccess={handle2FAComplete}
          onCancel={() => { setShow2FA(false); setTempToken(null); }}
        />
      )}
    </>
  );
};

export default Login;
